import { NavLink, Outlet, useNavigate } from 'react-router-dom'
import { Globe as Earth, Smartphone as Iphone, MapPin as Loc, AtSign as MailAddress, Network as Port, File, Key, List, ChevronLeft, ChevronRight, Server, FileCode, ShieldCheck, Cpu, DatabaseZap } from 'lucide-react'
import { useIntl } from 'react-intl'
import { useEffect, useState } from 'react'
import { TourButton } from '../../../tour/TourButton'
import { useFeature } from '../../../Utils/featureFlagEngine'
// Icon mapping for attack surface features
const ICON_MAP: Record<string, JSX.Element> = {
  Domain: <Earth size={22} className='svg-icon svg-icon-2x' />,
  IP: <Server size={22} className='svg-icon svg-icon-2x' />,
  Port: <Port size={22} className='svg-icon svg-icon-2x' />,
  'DNS Record': <FileCode size={22} className='svg-icon svg-icon-2x' />,
  SSL: <ShieldCheck size={22} className='svg-icon svg-icon-2x' />,
  Application: <Iphone size={22} className='svg-icon svg-icon-2x' />,
  Emails: <MailAddress size={22} className='svg-icon svg-icon-2x' />,
  'Leaked Data': <DatabaseZap size={22} className='svg-icon svg-icon-2x' />,
  'Technologies': <Cpu size={22} className='svg-icon svg-icon-2x' />,

}

export default function Explore() {
  const intl = useIntl()
  const navigate = useNavigate()
  const { getMainRoute, featureFlagEngine } = useFeature()
  const [isMinimized, setIsMinimized] = useState(false)

  // Get enabled attack surface children from feature flag engine
  const attackSurfaceChildren = featureFlagEngine
    .getChildFeatures('attack_surface')
    .filter((child) => child.route?.includes('/attack-surface-explore/'))

  // Build navigation items from enabled features
  const navItems = attackSurfaceChildren.map((feature) => {
    const routeSegment = feature.route?.split('/').pop() || ''
    return {
      route: routeSegment,
      name: feature.name,
      icon: ICON_MAP[feature.name] || <Earth size={22} className='svg-icon svg-icon-2x' />,
    }
  })

  // Redirect to dashboard if no attack surface features are enabled
  useEffect(() => {
    if (navItems.length === 0) {
      navigate(getMainRoute() || '/dashboard')
    }
  }, [navItems.length, navigate])

  // Show empty state if no features available
  if (navItems.length === 0) {
    return (
      <div className='d-flex flex-column justify-content-center align-items-center p-10 h-100'>
        <h3 className='text-muted text-nowrap'>No Attack Surface Features Available</h3>
        <p className='text-gray-600 text-nowrap'>
          All sub-features are currently disabled. Contact your administrator for access.
        </p>
      </div>
    )
  }

  return (
    <div className='position-relative d-flex flex-column flex-md-row w-100'>
      {/* Sidebar Navigation */}
      <aside
        className='position-sticky d-flex flex-column border-end border-gray-300 transition-all card align-self-start'
        style={{
          minWidth: isMinimized ? 80 : 200,
          maxWidth: isMinimized ? 80 : 200,
          top: 10,
          // Only the two properties the minimise toggle changes. `all` also
          // transitioned scrollbar-color on this scroll container, which is
          // inherited and not compositable.
          transition: 'min-width 0.3s ease-in-out, max-width 0.3s ease-in-out',
        }}
      >
        {/* Header Section */}
        <div data-tour="as-header" className='border-bottom d-flex justify-content-between align-items-center px-6 py-5 border-gray-300'>
          {!isMinimized && (
            <div className='d-flex flex-grow-1 align-items-center gap-2 overflow-hidden'>
              <h3 className='mb-1 text-gray-900 text-nowrap fs-5 fw-bold'>
                {intl.formatMessage({ id: 'Attack Surface' })}
              </h3>
              {/* <TourButton tourId='attackSurface' /> */}
            </div>
          )}
          <button
            onClick={() => setIsMinimized(!isMinimized)}
            className='rounded-circle btn btn-sm btn-icon btn-light-secondary'
            title={isMinimized ? 'Expand Sidebar' : 'Minimize Sidebar'}
          >
            {isMinimized ? <ChevronRight size={18} /> : <ChevronLeft size={18} />}
          </button>
        </div>

        {/* Navigation Menu */}
        <nav data-tour="as-nav" className='flex-grow-1 py-4 overflow-auto'>
          <div className='d-flex flex-column gap-2 px-4'>
            {navItems.map((item, ind) => (
              <NavLink
                className={({ isActive }: { isActive: boolean }) =>
                  `d-flex align-items-center ${isMinimized ? 'justify-content-center ' : 'gap-3'}  rounded text-decoration-none transition ${isActive
                    ? 'bg-primary bg-opacity-10 border border-primary text-primary'
                    : 'bg-hover-light-primary text-gray-700 hover-text-primary'
                  }`
                }
                to={item.route}
                key={ind}
                title={isMinimized ? intl.formatMessage({ id: item.name }) : ''}
              >
                <div className='d-flex flex-shrink-0 justify-content-center align-items-center' style={{ width: 40, height: 40 }}>
                  <div className='svg-icon-wrapper' style={{ width: 24, height: 24 }}>
                    {item.icon}
                  </div>
                </div>
                {!isMinimized && (
                  <span className='flex-grow-1 fs-6 fw-semibold'>
                    {intl.formatMessage({ id: item.name })}
                  </span>
                )}
              </NavLink>
            ))}
          </div>
        </nav>


      </aside>

      {/* Main Content Area */}
      <main data-tour="as-content" className='flex-grow-1 overflow-auto'>
        <Outlet />
      </main>
    </div>
  )
}
